import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft,
  Calendar,
  Plus,
  Trash2,
  AlertCircle,
  CheckCircle,
  Ban,
  Info
} from 'lucide-react';

const AdminBlackoutDatesPage = () => {
  const navigate = useNavigate();
  const [blackoutDates, setBlackoutDates] = useState([]);
  const [tourSchedule, setTourSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [blackoutType, setBlackoutType] = useState('single'); // 'single', 'range', or 'recurring'
  const [newBlackout, setNewBlackout] = useState({
    start_date: '',
    end_date: '',
    reason: '',
    is_recurring: false,
    recurring_day: null,
    blackout_times: [] // Empty array = all times, specific times = only those times
  });
  const [allTimesBlocked, setAllTimesBlocked] = useState(true); // true = block all tours, false = specific times only

  const tourTimes = [
    { value: '10:00', label: '10:00 AM' },
    { value: '14:00', label: '2:00 PM' },
    { value: '16:00', label: '4:00 PM' }
  ];

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      return null;
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    // Check authentication first
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchBlackoutDates();
    fetchTourSchedule();
  }, [navigate]);

  const fetchTourSchedule = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/settings/tour-schedule/public`
      );
      if (response.ok) {
        const data = await response.json();
        setTourSchedule(data);
      }
    } catch (err) {
      console.error('Error fetching tour schedule:', err);
    }
  };

  const fetchBlackoutDates = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      if (!headers) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/blackout-dates`,
        { headers }
      );

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch blackout dates');

      const data = await response.json();
      setBlackoutDates(data);
    } catch (err) {
      console.error('Error fetching blackout dates:', err);
      setError('Failed to load blackout dates');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBlackout = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (newBlackout.is_recurring) {
      if (newBlackout.recurring_day === null) {
        setError('Please select a day of the week');
        return;
      }
    } else {
      if (!newBlackout.start_date) {
        setError('Please select start date');
        return;
      }
      // Auto-set end_date to start_date if not provided (for single-day blackouts)
      if (!newBlackout.end_date) {
        setNewBlackout(prev => ({ ...prev, end_date: prev.start_date }));
      }
    }

    try {
      setSaving(true);
      const headers = getAuthHeaders();
      if (!headers) return;

      // Prepare payload - ensure end_date defaults to start_date for single-day blackouts
      const payload = {
        start_date: newBlackout.start_date || '2025-01-01', // Dummy date for recurring
        end_date: newBlackout.end_date || newBlackout.start_date || '2025-01-01',
        reason: newBlackout.reason || 'Tour unavailable',
        is_recurring: newBlackout.is_recurring,
        recurring_day: newBlackout.recurring_day,
        blackout_times: allTimesBlocked ? null : newBlackout.blackout_times
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/blackout-dates`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create blackout date');
      }

      setSuccess('Blackout date added successfully');
      setNewBlackout({ start_date: '', end_date: '', reason: '', is_recurring: false, recurring_day: null, blackout_times: [] });
      setAllTimesBlocked(true);
      fetchBlackoutDates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBlackout = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blackout date?')) {
      return;
    }

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/blackout-dates/${id}`,
        {
          method: 'DELETE',
          headers
        }
      );

      if (!response.ok) throw new Error('Failed to delete blackout date');

      setSuccess('Blackout date deleted successfully');
      fetchBlackoutDates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete blackout date');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isDateRangeSame = (start, end) => {
    return start === end;
  };

  const getDayName = (dayNumber) => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return days[dayNumber] || '';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Tour Blackout Dates</h1>
          <p className="text-gray-600 mt-2">
            Manage dates when online tour bookings are unavailable
          </p>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-green-800">{success}</span>
          </div>
        )}

        {/* Weekly Tour Schedule Rule (read-only reference) */}
        {tourSchedule && tourSchedule.restrict_monday_friday && (
          <Card className="mb-8 border-blue-200 bg-blue-50/50">
            <CardHeader>
              <CardTitle className="flex items-center text-blue-900">
                <Info className="w-5 h-5 mr-2" />
                Weekly Tour Schedule Rule
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-blue-900 space-y-3">
                <p>
                  <strong>Mondays &amp; Fridays only run the 4:00 PM tour</strong>
                  {tourSchedule.restriction_start_date && tourSchedule.restriction_end_date ? (
                    <> from {formatDate(tourSchedule.restriction_start_date)} to {formatDate(tourSchedule.restriction_end_date)}</>
                  ) : tourSchedule.restriction_start_date ? (
                    <> from {formatDate(tourSchedule.restriction_start_date)} onwards <span className="font-semibold">(ongoing — no end date)</span></>
                  ) : (
                    <> <span className="font-semibold">(ongoing — applies indefinitely)</span></>
                  )}
                  . The 10:00 AM and 2:00 PM tours are automatically hidden on those days (unless an exception below re-opens them).
                </p>

                {Array.isArray(tourSchedule.overrides) &&
                  tourSchedule.overrides.filter(o => o.enabled !== false).length > 0 && (
                    <div>
                      <p className="font-semibold mb-2">Exceptions that re-open extra times:</p>
                      <div className="space-y-2">
                        {tourSchedule.overrides
                          .filter(o => o.enabled !== false)
                          .map((o, i) => {
                            const days = [
                              o.apply_monday && 'Mondays',
                              o.apply_friday && 'Fridays'
                            ].filter(Boolean).join(' & ');
                            const times = [
                              o.allow_morning && '10:00 AM',
                              o.allow_afternoon && '2:00 PM'
                            ].filter(Boolean).join(' & ');
                            if (!days || !times) return null;
                            return (
                              <div
                                key={i}
                                className="bg-white border border-blue-200 rounded p-2 text-xs text-gray-700"
                              >
                                <span className="font-medium text-blue-800">
                                  {o.label || 'Exception'}
                                </span>
                                : {days} re-open {times}
                                {o.start_date && o.end_date && (
                                  <> ({formatDate(o.start_date)} → {formatDate(o.end_date)})</>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                <p className="text-xs text-blue-700 border-t border-blue-200 pt-2">
                  ℹ️ This is a standing weekly rule, <strong>not</strong> a blackout date — it's managed under{' '}
                  <strong>Admin Settings → Tour Schedule</strong> and is shown here for reference only.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Add New Blackout Date */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Plus className="w-5 h-5 mr-2" />
              Add Blackout Date
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddBlackout} className="space-y-4">
              {/* Blackout Type Selection */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Blackout Type *</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setBlackoutType('single');
                      setNewBlackout(prev => ({ ...prev, is_recurring: false, recurring_day: null }));
                    }}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      blackoutType === 'single'
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-300 hover:border-orange-300'
                    }`}
                  >
                    <div className="font-semibold text-orange-700">📅 Single Date</div>
                    <div className="text-xs text-gray-600 mt-1">Block one specific day</div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      setBlackoutType('range');
                      setNewBlackout(prev => ({ ...prev, is_recurring: false, recurring_day: null }));
                    }}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      blackoutType === 'range'
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-300 hover:border-orange-300'
                    }`}
                  >
                    <div className="font-semibold text-orange-700">📆 Date Range</div>
                    <div className="text-xs text-gray-600 mt-1">Block multiple consecutive days</div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      setBlackoutType('recurring');
                      setNewBlackout(prev => ({ ...prev, is_recurring: true, recurring_day: 1 }));
                    }}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      blackoutType === 'recurring'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-blue-300'
                    }`}
                  >
                    <div className="font-semibold text-blue-700">🔄 Recurring Weekly</div>
                    <div className="text-xs text-gray-600 mt-1">Block every week (e.g., Tuesdays)</div>
                  </button>
                </div>
              </div>

              {/* Tour Times Selection - shown for all blackout types */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Which tours to block?</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setAllTimesBlocked(true);
                      setNewBlackout(prev => ({ ...prev, blackout_times: [] }));
                    }}
                    className={`p-3 border-2 rounded-lg text-left transition-all ${
                      allTimesBlocked
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-300 hover:border-red-300'
                    }`}
                  >
                    <div className="font-semibold text-red-700">🚫 All Tours</div>
                    <div className="text-xs text-gray-600 mt-1">Block all tour times on this date</div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setAllTimesBlocked(false)}
                    className={`p-3 border-2 rounded-lg text-left transition-all ${
                      !allTimesBlocked
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-300 hover:border-amber-300'
                    }`}
                  >
                    <div className="font-semibold text-amber-700">⏰ Specific Times Only</div>
                    <div className="text-xs text-gray-600 mt-1">Block only selected tour times</div>
                  </button>
                </div>

                {!allTimesBlocked && (
                  <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                    <Label className="text-sm font-medium mb-2 block">Select times to block:</Label>
                    <div className="flex flex-wrap gap-3">
                      {tourTimes.map((time) => (
                        <label key={time.value} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newBlackout.blackout_times.includes(time.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewBlackout(prev => ({
                                  ...prev,
                                  blackout_times: [...prev.blackout_times, time.value]
                                }));
                              } else {
                                setNewBlackout(prev => ({
                                  ...prev,
                                  blackout_times: prev.blackout_times.filter(t => t !== time.value)
                                }));
                              }
                            }}
                            className="w-4 h-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                          />
                          <span className="text-sm font-medium text-gray-700">{time.label}</span>
                        </label>
                      ))}
                    </div>
                    {newBlackout.blackout_times.length === 0 && (
                      <p className="text-xs text-amber-700 mt-2">⚠️ Please select at least one time</p>
                    )}
                  </div>
                )}
              </div>

              {blackoutType === 'recurring' ? (
                /* Recurring Blackout Form */
                <div className="space-y-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div>
                    <Label htmlFor="recurring_day">Day of Week *</Label>
                    <select
                      id="recurring_day"
                      value={newBlackout.recurring_day || 1}
                      onChange={(e) => setNewBlackout(prev => ({ ...prev, recurring_day: parseInt(e.target.value) }))}
                      className="w-full p-2 border rounded"
                      data-testid="recurring-day-select"
                    >
                      <option value="0">Every Monday</option>
                      <option value="1">Every Tuesday</option>
                      <option value="2">Every Wednesday</option>
                      <option value="3">Every Thursday</option>
                      <option value="4">Every Friday</option>
                      <option value="5">Every Saturday</option>
                      <option value="6">Every Sunday</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="reason">Reason (Optional)</Label>
                    <Textarea
                      id="reason"
                      placeholder="e.g., Staff day off, Regular maintenance"
                      value={newBlackout.reason}
                      onChange={(e) => setNewBlackout(prev => ({ ...prev, reason: e.target.value }))}
                      rows={2}
                    />
                  </div>
                  <p className="text-sm text-blue-700">
                    ℹ️ This will block online bookings every week on this day
                  </p>
                </div>
              ) : blackoutType === 'single' ? (
                /* Single Date Form */
                <div className="space-y-4 bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <div>
                    <Label htmlFor="single_date">Date *</Label>
                    <Input
                      id="single_date"
                      type="date"
                      value={newBlackout.start_date}
                      onChange={(e) => setNewBlackout(prev => ({
                        ...prev,
                        start_date: e.target.value,
                        end_date: e.target.value // Automatically set end date to same as start
                      }))}
                      required
                      data-testid="single-date-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reason">Reason (Optional)</Label>
                    <Textarea
                      id="reason"
                      placeholder="e.g., Public holiday, Special event"
                      value={newBlackout.reason}
                      onChange={(e) => setNewBlackout(prev => ({ ...prev, reason: e.target.value }))}
                      rows={3}
                      data-testid="reason-input"
                    />
                  </div>
                </div>
              ) : (
                /* Date Range Form */
                <div className="space-y-4 bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="start_date">Start Date *</Label>
                      <Input
                        id="start_date"
                        type="date"
                        value={newBlackout.start_date}
                        onChange={(e) => setNewBlackout(prev => ({ 
                          ...prev, 
                          start_date: e.target.value
                        }))}
                        required
                        data-testid="start-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="end_date">End Date *</Label>
                      <Input
                        id="end_date"
                        type="date"
                        value={newBlackout.end_date}
                        onChange={(e) => setNewBlackout(prev => ({ ...prev, end_date: e.target.value }))}
                        min={newBlackout.start_date}
                        required
                        data-testid="end-date-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="reason">Reason (Optional)</Label>
                    <Textarea
                      id="reason"
                      placeholder="e.g., School holidays, Maintenance period"
                      value={newBlackout.reason}
                      onChange={(e) => setNewBlackout(prev => ({ ...prev, reason: e.target.value }))}
                      rows={3}
                      data-testid="reason-input"
                    />
                  </div>
                </div>
              )}

              <Button type="submit" disabled={saving} data-testid="add-blackout-btn">
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Blackout Date
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Blackout Dates List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Ban className="w-5 h-5 mr-2" />
              Current Blackout Dates
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading...</p>
              </div>
            ) : blackoutDates.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600">No blackout dates configured</p>
              </div>
            ) : (
              <div className="space-y-3">
                {blackoutDates.map((blackout) => (
                  <div
                    key={blackout.id}
                    className={`flex items-center justify-between p-4 rounded-lg border hover:border-gray-300 transition ${
                      blackout.is_recurring 
                        ? 'bg-blue-50 border-blue-200' 
                        : 'bg-gray-50 border-gray-200'
                    }`}
                    data-testid={`blackout-${blackout.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {blackout.is_recurring ? (
                          <>
                            <Calendar className="w-4 h-4 text-blue-600" />
                            <span className="font-semibold text-blue-900">
                              🔄 Every {getDayName(blackout.recurring_day)}
                            </span>
                            <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">
                              Recurring
                            </span>
                          </>
                        ) : (
                          <>
                            <Calendar className="w-4 h-4 text-purple-600" />
                            <span className="font-semibold text-gray-900">
                              {isDateRangeSame(blackout.start_date, blackout.end_date) ? (
                                formatDate(blackout.start_date)
                              ) : (
                                `${formatDate(blackout.start_date)} → ${formatDate(blackout.end_date)}`
                              )}
                            </span>
                          </>
                        )}
                      </div>
                      {/* Show which times are blocked */}
                      <div className="ml-6 mt-1">
                        {blackout.blackout_times && blackout.blackout_times.length > 0 ? (
                          <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded inline-flex items-center gap-1">
                            ⏰ {blackout.blackout_times.map(t => {
                              const time = tourTimes.find(tt => tt.value === t);
                              return time ? time.label : t;
                            }).join(', ')}
                          </span>
                        ) : (
                          <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded inline-flex items-center gap-1">
                            🚫 All tours blocked
                          </span>
                        )}
                      </div>
                      {blackout.reason && (
                        <p className="text-sm text-gray-600 ml-6 mt-1">{blackout.reason}</p>
                      )}
                      <p className="text-xs text-gray-500 ml-6 mt-1">
                        Added by {blackout.created_by}
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteBlackout(blackout.id)}
                      data-testid={`delete-blackout-${blackout.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminBlackoutDatesPage;
